import type { LogLevel, LogEntry, LogContext, LoggerConfig } from "./types";
import { getLoggerConfig, shouldLog } from "./config";
import { serializeError, sanitizeLogData } from "./serializer";

interface LoggerDependencies {
  config: LoggerConfig;
  output: (entry: LogEntry) => void;
  shouldLog: (level: LogLevel) => boolean;
}

class Logger {
  constructor(private readonly deps: Readonly<LoggerDependencies>) {}

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: unknown,
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message: String(message),
      environment: this.deps.config.environment,
      version: this.deps.config.version,
    };

    if (context) {
      entry.context = {
        ...context,
        metadata: context.metadata
          ? (sanitizeLogData(context.metadata) as Record<string, unknown>)
          : undefined,
      };
    }

    if (error) {
      entry.error = serializeError(error);
    }

    return entry;
  }

  private log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: unknown,
  ): void {
    if (!this.deps.shouldLog(level)) return;
    if (!this.deps.config.enableConsole) return;

    const entry = this.createLogEntry(level, message, context, error);
    this.deps.output(entry);
  }

  debug(message: string, context?: LogContext): void {
    this.log("debug", message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log("info", message, context);
  }

  warn(message: string, context?: LogContext, error?: unknown): void {
    this.log("warn", message, context, error);
  }

  error(message: string, context?: LogContext, error?: unknown): void {
    this.log("error", message, context, error);
  }

  fatal(message: string, context?: LogContext, error?: unknown): void {
    this.log("fatal", message, context, error);
  }
}

// Factory function for creating logger instances
export const createLogger = (deps?: Partial<LoggerDependencies>): Logger => {
  return new Logger({
    config: deps?.config ?? getLoggerConfig(),
    output: deps?.output ?? console.log,
    shouldLog: deps?.shouldLog ?? shouldLog,
  });
};

// Default logger instance
export const logger = createLogger();

// Export convenience functions
export const log = {
  debug: (message: string, context?: LogContext) =>
    logger.debug(message, context),
  info: (message: string, context?: LogContext) =>
    logger.info(message, context),
  warn: (message: string, context?: LogContext, error?: unknown) =>
    logger.warn(message, context, error),
  error: (message: string, context?: LogContext, error?: unknown) =>
    logger.error(message, context, error),
  fatal: (message: string, context?: LogContext, error?: unknown) =>
    logger.fatal(message, context, error),
};

// Export types and factory for testing
export type { LoggerDependencies };
